import React, { JSX } from "react";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import "./styling/App.scss";
import { useAppSelector, useAppDispatch } from "./app/hooks";
import { Container, Row, Col, Nav, Navbar, OverlayTrigger, Tooltip } from "react-bootstrap";
import SXMobileWarning from "@common/components/ui-components/SXMobileWarning";
import LandingPage from "./LandingPage";
import ProjectExplorer from "./ProjectExplorer";
import ApplicationsPage from "./ApplicationsPage";
import { SimplexSvgLogo } from "./assets/SimplexSvgLogo";
import { SettingsIcon } from "./assets/SettingsIcon";
import { UserInitialsIcon } from "./assets/UserInitialsIcon";
import { useUser, selectUser } from "./features/user/userSlice";
import "../i18n";
import { useTranslation } from "react-i18next";
import LoginButton from "./components/LoginButton";
import { SXOffCanvasMenu } from "./components/SXOffCanvasMenu";
import { SettingsModal } from "./components/modals/SettingsModal";
import { ProjectCreateModal } from "./components/modals/ProjectCreateModal";
import { ReleaseNotesModal } from "./components/modals/ReleaseNotesModal";
import { LatestReleaseNotesModal } from "./components/modals/LatestReleaseNotesModal";
import { IReleaseNotes } from "@common/types/sharedTypes";
import ProjectExplorerSkeleton from "./components/ProjectExplorer/ProjectExplorerSkeleton";

import {
  openModal,
  closeModal,
  selectModalById,
  selectShowOffCanvas,
  openOffCanvas,
  closeOffCanvas,
  selectActiveLink,
  setActiveLink,
  selectFullscreen,
  selectIsMobile,
  setIsMobile,
  selectBreakPoint,
} from "./features/ui/uiSlice";
import { ProjectSlice } from "./features/projects/projectSlice";
// Importing hooks
import { toggleFullscreenThunk } from "./features/ui/uiThunks";
import { FullScreenToggleIcon } from "./assets/FullScreenToggleIcon";
import { v4 as uuidv4 } from "uuid";
import { BenchmarkModal } from "./components/modals/BenchmarkModal";
import { selectIsSalesUser } from "./features/user/userSlice";
import { ShareModal } from "./components/modals/ShareModal";

// Reuses the overlay/spinner styling from sx-common-ts' SXLoadingMessages (as seen in sx-column),
// but without its wrapping Navbar/Container shell: App already renders its own top nav bar while
// `currentPage` is still loading, so a second one would duplicate the chrome and the app_container id.
function AppLoadingOverlay() {
  const { t } = useTranslation();
  return (
    <div id="overlay-spinner" style={{ background: "rgba(0, 0, 0, 0.5)", top: 50 }}>
      <div className="overlay_inner">
        <div className="overlay_panel">
          <div className="overlay_indicator">
            <span className="spinner" />
          </div>
          <div className="overlay_message_box">
            <h4 className="overlay_title">{t("loading_app_.label", "Loading")}</h4>
            <p className="overlay_message_text">
              {t("loading_app_please_wait_.label", "Please wait while we prepare the app.")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function getClientId(): string {
  const cookieName = "simplex-client-id";
  const match = document.cookie.match(new RegExp("(^| )" + cookieName + "=([^;]+)"));
  let clientId = match ? match[2] : null;

  // Determine parent domain for cookie
  let cookieDomain = "";
  if (window.location.hostname.endsWith(".private.strusoft.com")) {
    cookieDomain = "domain=.private.strusoft.com;";
  } else if (window.location.hostname.endsWith(".onstrusoft.com")) {
    cookieDomain = "domain=.onstrusoft.com;";
  }
  // For localhost, do not set domain

  if (!clientId) {
    clientId = crypto.randomUUID();
    document.cookie = `${cookieName}=${clientId}; ${cookieDomain} path=/; max-age=${10 * 365 * 24 * 60 * 60}`;
  }
  return clientId;
}

const RELEASE_NOTES_STORAGE_KEY = "sx-center-last-release-note-shown";

function App() {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();
  const { getUser } = useUser();
  const user = useAppSelector(selectUser);
  const isSalesUser = useAppSelector(selectIsSalesUser);

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<JSX.Element | null>(null);
  const [showBenchmark, setShowBenchmark] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState<IReleaseNotes | undefined>(undefined);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showLatestReleaseNotes, setShowLatestReleaseNotes] = useState(false);
  const { isLoading, isAuthenticated } = useAuth0();
  //selectors
  const showOffCanvas = useSelector(selectShowOffCanvas);
  const isFullscreen = useSelector(selectFullscreen);
  const isMobile = useSelector(selectIsMobile);
  const breakpoint = useSelector(selectBreakPoint);
  const activeLink = useSelector(selectActiveLink);
  const isSettingsModalOpen = useSelector(selectModalById("settingsModal"));
  const isCreateModalOpen = useSelector(selectModalById("createModal"));

  useEffect(() => {
    async function fetchData() {
      try {
        if (!isLoading && !isAuthenticated) {
          // Auth0 has resolved and there's no session: nothing more to wait for, show the
          // logged-out landing/applications page straight away.
          setLoading(false);
        }
      } catch (e) {
        console.log(e);
        //throw e;
        // Optionally log error
      } finally {
        getUser();
      }
    }
    fetchData();
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const response = await fetch("/api/releaseNotes");
        if (!response.ok) {
          throw new Error(`Failed to fetch release notes: ${response.status}`);
        }

        setReleaseNotes((await response.json()) as IReleaseNotes);
      } catch (error) {
        console.error(error);
      }
    };

    fetchReleaseNotes();
  }, []);

  useEffect(() => {
    // Example: set language from localStorage or default to 'en'
    const lang = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(lang);
  }, [i18n]);

  /* check for mobile size screen, if so, show alert*/
  useEffect(() => {
    const handleResize = () => {
      dispatch(setIsMobile(window.innerWidth < breakpoint));
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, breakpoint]);

  useEffect(() => {
    if (!loading) {
      dispatch(setActiveLink(user ? "ProjectExplorer" : "Applications"));
      setCurrentPage(user ? <ProjectExplorer /> : <ApplicationsPage />);
    }
  }, [user, loading]);

  // Flips `loading` off once we actually know what to show: either the user's project data has
  // arrived (ProjectExplorer) or Auth0 settled on "authenticated" with no user yet resolved is
  // still handled above. This used to be done by the auth0-only effect above, whose condition was
  // true on the very first render regardless of real auth/user state, so `loading` went false
  // almost instantly and the loading overlay/skeleton was never actually visible for the time it
  // takes `fetchUser` (which returns the whole projectdigest in one shot) to resolve.
  useEffect(() => {
    if (user) {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const latestVersion = releaseNotes?.releases?.[0]?.version;
    if (!latestVersion || !user?.user?.settings) {
      return;
    }

    const lastShown = localStorage.getItem(RELEASE_NOTES_STORAGE_KEY) || user.user.settings.lastReleaseNoteShown;
    if (lastShown !== latestVersion) {
      setShowLatestReleaseNotes(true);
    }
  }, [releaseNotes, user]);

  const handleCloseLatestReleaseNotes = () => {
    const latestVersion = releaseNotes?.releases?.[0]?.version;
    if (latestVersion) {
      localStorage.setItem(RELEASE_NOTES_STORAGE_KEY, latestVersion);
    }
    setShowLatestReleaseNotes(false);
  };

  const handleLinkClick = (linkId: string, ComponentPage: JSX.Element) => (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setActiveLink(linkId));
    setCurrentPage(ComponentPage);
  };

  return (
    <Container fluid id="app_container">
      {isMobile && <SXMobileWarning />}
      <Navbar id="top-nav-bar" className="titleRow no-print">
        <Navbar.Brand
          style={{ width: 115 }}
          onClick={(e) => {
            e.preventDefault();
            setCurrentPage(<LandingPage />);
          }}
        >
          <SimplexSvgLogo />
        </Navbar.Brand>
        <Nav className="title-nav">
          <a href="/" id="app-title" style={{ height: 40 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="25px" height="25px" viewBox="0 25 850.39 850.39">
              <path
                fill="white"
                d="M787.77,272.02c-19.82-46.86-48.19-88.94-84.32-125.07-36.13-36.13-78.21-64.5-125.07-84.32-48.53-20.53-100.06-30.93-153.18-30.93s-104.65,10.41-153.18,30.93c-46.86,19.82-88.94,48.19-125.07,84.32s-64.5,78.21-84.32,125.07c-20.53,48.53-30.93,100.07-30.93,153.18s10.41,104.65,30.93,153.18c19.82,46.86,48.19,88.94,84.32,125.07,36.13,36.13,78.21,64.5,125.07,84.32,48.53,20.53,100.07,30.93,153.18,30.93s104.65-10.41,153.18-30.93c46.86-19.82,88.94-48.19,125.07-84.32,36.13-36.13,64.5-78.21,84.32-125.07,20.53-48.53,30.93-100.07,30.93-153.18s-10.41-104.65-30.93-153.18ZM264.5,121.65c-5.88,35.37-23.6,103.35-76.7,173.07-38.42,50.45-71.94,76.51-93.29,89.49-3.96,2.41-7.66,4.47-11.07,6.22,11.77-116.6,82.12-216.18,181.06-268.78ZM425.2,768.7c-182.65,0-332.46-143.3-342.92-323.37,27.64-9.35,82.07-37.3,145.3-120.32,23.06-30.27,42.05-62.63,56.76-96.65,7.88,30.25,19.32,64.16,35.88,100.08,28.34,61.47,57.65,125.03,97.82,174.12,47.44,57.98,102.84,86.17,169.35,86.17,60.01,0,118.82-26.5,170.06-76.65.05-.05.11-.11.16-.16-38.53,147.55-172.96,256.78-332.41,256.78ZM721.52,477.28c-42.04,40.78-87.17,61.46-134.13,61.46-51.27,0-92.79-21.55-130.65-67.83-36.13-44.16-64.08-104.78-91.11-163.4-44.86-97.28-48.42-178.66-48.42-202.04,0-2.7.05-4.62.08-5.71,0,0,0,0,0,0,.01-.26.02-.5.03-.71,33.94-11.25,70.2-17.34,107.87-17.34,187.49,0,340.38,150.99,343.46,337.76-7.16,11.28-23.33,34.75-47.13,57.83Z"
              />
            </svg>
          </a>
          <Nav.Link></Nav.Link>
          {user && (
            <Nav.Link
              className={`nav-link hover-underline-animation ${activeLink === "ProjectExplorer" ? "active-link" : ""}`}
              onClick={handleLinkClick("ProjectExplorer", <ProjectExplorer />)}
            >
              {t("projects")}
            </Nav.Link>
          )}
          <Nav.Link
            className={`nav-link hover-underline-animation ${activeLink === "Applications" ? "active-link" : ""}`}
            onClick={handleLinkClick("Applications", <ApplicationsPage user={user} />)}
          >
            {t("applications")}
          </Nav.Link>
        </Nav>
        <Nav className="me-auto"></Nav>
        <Nav>
          {" "}
          {user && (
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 700, hide: 160 }}
              overlay={<Tooltip id="tooltip">{t("fullscreen")}</Tooltip>}
            >
              <Nav.Link onClick={() => dispatch(toggleFullscreenThunk())}>
                <FullScreenToggleIcon isFullscreen={isFullscreen} />
              </Nav.Link>
            </OverlayTrigger>
          )}
        </Nav>
        <Nav>
          {user && (
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 700, hide: 160 }}
              overlay={<Tooltip id="tooltip">{t("settings")}</Tooltip>}
            >
              <Nav.Link
                className="hover-underline-animation"
                id={"showsettingsdialog"}
                onClick={() => {
                  // open settingsmodal
                  dispatch(openModal("settingsModal"));
                }}
              >
                <SettingsIcon />
              </Nav.Link>
            </OverlayTrigger>
          )}
        </Nav>
        <Nav>
          {user ? (
            <Nav.Link onClick={() => dispatch(openOffCanvas())} style={{ paddingTop: 8 }}>
              <UserInitialsIcon user={user.user} />
            </Nav.Link>
          ) : (
            <Nav.Link className="nav-link hover-underline-animation">
              <LoginButton id={"loginbutton"} />
            </Nav.Link>
          )}
        </Nav>
      </Navbar>
      <SXOffCanvasMenu
        showOffCanvas={showOffCanvas}
        handleCloseOffCanvas={() => dispatch(closeOffCanvas())}
        user={user?.user}
        isSalesUser={isSalesUser}
        onShowBenchmark={() => setShowBenchmark(true)}
        onShowReleaseNotes={() => setShowReleaseNotes(true)}
      />
      <BenchmarkModal show={showBenchmark} onHide={() => setShowBenchmark(false)} />
      {/* Main Application Row */}
      <Row id="main-app-row">
        <Container
          id="main-content"
          className={
            currentPage?.type === ProjectExplorer || (loading && isAuthenticated)
              ? "project-x-bg pt-1 ps-1 pe-1 forced-scroll"
              : "default-bg"
          }
        >
          {!loading ? currentPage : isAuthenticated ? <ProjectExplorerSkeleton /> : <AppLoadingOverlay />}
        </Container>
      </Row>
      <SettingsModal isSettingsModalOpen={isSettingsModalOpen} />
      <ProjectCreateModal isCreateModalOpen={isCreateModalOpen} />
      {releaseNotes && (
        <ReleaseNotesModal close={() => setShowReleaseNotes(false)} show={showReleaseNotes} notes={releaseNotes} />
      )}
      {releaseNotes && user?.user?.settings && (
        <LatestReleaseNotesModal
          close={handleCloseLatestReleaseNotes}
          show={showLatestReleaseNotes}
          notes={releaseNotes}
        />
      )}
    </Container>
  );
}

export default App;
